import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ListChecks, Plus, RefreshCw, Search } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import QuarterPicker from '../components/QuarterPicker';
import DownloadMenu from '../components/DownloadMenu';
import FlowTabs from '../components/flow/FlowTabs';
import InfoTip from '../components/InfoTip';
import PlanTable from '../components/plan/PlanTable';
import RoadmapGrid from '../components/plan/RoadmapGrid';
import FunctionMatrix from '../components/plan/FunctionMatrix';
import IntakeList from '../components/plan/IntakeList';
import PlanRowPanel from '../components/plan/PlanRowPanel';
import AddIdeaPanel from '../components/plan/AddIdeaPanel';
import { GROUP_PAGE, canEditRow, findRow, headline, searchRows } from '../components/plan/model';
import { PlanData, PlanRow } from '../components/plan/types';
import { apiService } from '../services/api';
import { useToast } from '../contexts/ToastContext';
import { useTabParam } from '../hooks/useTabParam';
import { useBoundaries } from '../hooks/useBoundaries';
import { usePeriodParams } from '../hooks/usePeriodParams';
import { useUrlState } from '../hooks/useUrlState';
import { navLabel } from '../utils/navigation';
import { saveBlob } from '../utils/download';
import { projectParam, quarterParam } from '../utils/urlParams';
import * as fmt from '../lib/format';

type TabId = 'project' | 'roadmap' | 'function' | 'intake' | 'adhoc' | 'ideation' | 'before';
const TAB_IDS: TabId[] = ['project', 'roadmap', 'function', 'intake', 'adhoc', 'ideation', 'before'];

const CARD = 'rounded-2xl bg-white shadow-sm ring-1 ring-gray-200/70 contrast-more:ring-gray-400';
const OPEN_ID = /^(idea:\d{1,9}|[A-Z][A-Z0-9_]+-\d{1,9})$/;

// The Plan: the quarter planning sheet, made in the portal. One group per project, as the sheet had them; the
// quarter's unplanned asks and the asks from elsewhere are their own tabs. Everyone reads it and may add an idea;
// leaders, admins and a project's owner change a row.
export default function PlanPage() {
  const { params, update } = useUrlState();
  const { showToast } = useToast();
  const [tab, setTab] = useTabParam<TabId>(TAB_IDS, 'project');
  const { period, current: currentMonth, setPeriod } = usePeriodParams();
  const boundaries = useBoundaries();
  const quarterId = quarterParam(params);
  const projectKey = projectParam(params);
  const openParam = params.get('open');
  const openId = openParam && OPEN_ID.test(openParam) ? openParam : null;
  // Which groups show every row, not the first few: ?all=SRL,VO (a section is its own name).
  const expanded = useMemo(() => new Set((params.get('all') ?? '').split(',').filter(Boolean)), [params]);

  const [data, setData] = useState<PlanData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [adding, setAdding] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await apiService.getPlan(quarterId, projectKey);
      if (!r.success) throw new Error(r.error);
      setData(r.data);
      setError(null);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Could not read the plan');
    } finally {
      setLoading(false);
    }
  }, [quarterId, projectKey]);

  useEffect(() => {
    setLoading(true);
    void load();
  }, [load]);

  const label = data ? fmt.quarter(data.quarter.id) : quarterId ? fmt.quarter(quarterId) : 'this quarter';
  const pickerValue = (data?.quarter.id ?? quarterId)?.replace('-Q', '-') ?? '';
  const row = data ? findRow(data, openId) : null;
  const setOpen = (id: string | null) => update({ open: id });
  const toggleAll = (key: string) => {
    const next = new Set(expanded);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    update({ all: next.size ? Array.from(next).join(',') : null });
  };

  const download = async () => {
    if (!data) return;
    setDownloading(true);
    try {
      saveBlob(await apiService.downloadPlan(data.quarter.id), `plan-${data.quarter.id}.csv`);
    } catch (e: any) {
      showToast('error', e?.message || 'Could not download the plan');
    } finally {
      setDownloading(false);
    }
  };

  const counts = data ? { project: data.projects.reduce((n, g) => n + g.rows.length, 0), adhoc: data.adhoc.length, ideation: data.ideation.length } : null;
  const tabs = [
    { id: 'project', label: 'By project', count: counts?.project },
    { id: 'roadmap', label: 'Roadmap', tip: 'plan.roadmap' as const },
    { id: 'function', label: 'By function', tip: 'plan.function' as const },
    { id: 'intake', label: 'Intake', tip: 'plan.intake' as const },
    { id: 'adhoc', label: 'Ad hoc', count: counts?.adhoc, tip: 'plan.adhoc' as const },
    { id: 'ideation', label: 'Ideation', count: counts?.ideation, tip: 'plan.ideation' as const },
    // Parked apart: the months before the Jira conventions started (the day is a setting, so the name is read from it).
    ...(data && data.before.length > 0 ? [{ id: 'before', label: boundaries?.label ?? 'Before the Jira conventions', count: data.before.length, tip: 'plan.before' as const }] : []),
  ];
  // The quarter and the search belong to the lenses that read a quarter; Intake reads a month, and Before reads neither.
  const quarterTabs: TabId[] = ['project', 'roadmap', 'function', 'adhoc', 'ideation'];
  const searchTabs: TabId[] = ['project', 'adhoc', 'ideation', 'intake', 'before'];

  const section = (key: string, name: string, rows: PlanRow[], showProject: boolean) => {
    const found = searchRows(rows, query);
    if (query && found.length === 0) return null;
    const all = expanded.has(key) || !!query || found.length <= GROUP_PAGE;
    const shown = all ? found : found.slice(0, GROUP_PAGE);
    return (
      <section key={key} className={`${CARD} mb-5 overflow-hidden`} aria-label={name}>
        <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 px-5 pt-4 pb-2">
          <h2 className="min-w-0 break-words text-base font-semibold text-gray-900">{name}</h2>
          <span className="text-xs text-gray-600">{fmt.count(found.length)} {found.length === 1 ? 'item' : 'items'}</span>
        </div>
        <PlanTable caption={name} rows={shown} openId={openId} onOpen={setOpen} showProject={showProject} />
        {found.length > GROUP_PAGE && !query && (
          <div className="border-t border-gray-100 px-5 py-2.5">
            <button
              type="button"
              onClick={() => toggleAll(key)}
              className="rounded text-sm font-medium text-blue-700 hover:text-blue-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            >
              {expanded.has(key) ? `Show the first ${GROUP_PAGE}` : `Show all ${fmt.count(found.length)} items`}
            </button>
          </div>
        )}
      </section>
    );
  };

  const nothing = (text: string) => <div className={`${CARD} p-8 text-center text-sm text-gray-600`}>{text}</div>;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100/60">
      <PageHeader title={navLabel('/plan')} subtitle="The quarter's plan, project by project" icon={ListChecks} />
      <div className="p-4 sm:p-6">
        <div className="mx-auto max-w-7xl">
          <div className="mb-5 flex flex-wrap items-center gap-3">
            {quarterTabs.includes(tab) && <QuarterPicker value={pickerValue} onChange={(v) => update({ quarter: v.replace('-', '-Q'), open: null })} className="" />}
            <label htmlFor="plan-project" className="sr-only">Project</label>
            <select
              id="plan-project"
              value={projectKey ?? ''}
              onChange={(e) => update({ project_key: e.target.value || null, open: null })}
              className="max-w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-100"
            >
              <option value="">All projects</option>
              {(data?.project_options ?? []).map((p) => (
                <option key={p.project_key} value={p.project_key}>{p.project_name}</option>
              ))}
            </select>
            {searchTabs.includes(tab) && (
            <div className="relative min-w-[10rem] flex-1 sm:max-w-xs">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" aria-hidden />
              <label htmlFor="plan-search" className="sr-only">Search the plan</label>
              <input
                id="plan-search"
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search tasks, tickets, people"
                className="w-full rounded-xl border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-100"
              />
            </div>
            )}
            <div className="ml-auto flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setAdding(true)}
                className="inline-flex items-center gap-1.5 rounded-xl bg-blue-700 px-3 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-blue-800 active:scale-[0.98] motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
              >
                <Plus className="h-4 w-4" aria-hidden />
                Add an idea
              </button>
              <DownloadMenu
                busy={downloading}
                disabled={!data}
                items={[{ key: 'csv', title: 'Plan (.csv)', hint: "The sheet's columns and sections, so the sheet can keep being used", onSelect: download }]}
              />
              <button
                type="button"
                onClick={() => void load()}
                aria-label="Refresh"
                className="rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-white hover:text-gray-900 active:scale-[0.96] motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
              >
                <RefreshCw className="h-4 w-4" />
              </button>
            </div>
          </div>

          {error && (
            <div role="alert" className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-rose-50 p-4 text-sm text-rose-800 ring-1 ring-rose-200">
              <span>{error}</span>
              <button type="button" onClick={() => void load()} className="rounded-lg border border-rose-300 bg-white px-3 py-1.5 font-medium text-rose-800 hover:bg-rose-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-500">
                Try again
              </button>
            </div>
          )}

          {data && tab === 'project' && (
            <p className="mb-5 flex flex-wrap items-center text-sm text-gray-700">
              <span className="font-semibold text-gray-900">{label}.</span>&nbsp;{headline(data.projects, label)}
              <InfoTip id="plan.status" />
            </p>
          )}

          <FlowTabs tabs={tabs} active={tab} onChange={(id) => setTab(id as TabId)} label="Plan views" idPrefix="plan" />

          <div role="tabpanel" id={`plan-panel-${tab}`} aria-labelledby={`plan-tab-${tab}`}>
            {loading && !data && tab !== 'intake' && <div className={`${CARD} p-8 text-center text-sm text-gray-600`}>Reading the plan…</div>}
            {data && tab === 'project' && (data.projects.length === 0 ? nothing(`Nothing is planned for ${label} yet. Add an idea, or import the sheet in Settings.`) : data.projects.map((g) => section(g.project_key ?? g.project_name, g.project_name, g.rows, false)))}
            {data && tab === 'adhoc' && (data.adhoc.length === 0 ? nothing(`No unplanned asks for ${label} yet.`) : section('adhoc', 'Ad hoc tasks', data.adhoc, true))}
            {tab === 'roadmap' && <RoadmapGrid quarter={quarterId} projectKey={projectKey} onOpen={(key) => setOpen(key)} />}
            {tab === 'function' && <FunctionMatrix quarter={quarterId} projectKey={projectKey} onOpen={(key) => setOpen(key)} />}
            {tab === 'intake' && <IntakeList period={period} onPeriod={setPeriod} max={currentMonth} projectKey={projectKey} query={query} expanded={expanded} onToggleAll={toggleAll} />}
            {data && tab === 'before' && (
              <>
                <p className="mb-4 text-sm text-gray-700">
                  These roadmap items finished before the Jira conventions started on {fmt.date(data.tech_from)}. They are kept apart, and never mixed into the plan or compared with later work.
                </p>
                {section('before', boundaries?.label ?? 'Before the Jira conventions', data.before, true)}
              </>
            )}
            {data && tab === 'ideation' && (data.ideation.length === 0 ? nothing('No asks from elsewhere yet.') : section('ideation', 'Additional tasks (ideation)', data.ideation, true))}
          </div>
        </div>
      </div>

      <PlanRowPanel row={row} canEdit={!!data && !!row && canEditRow(data.can_edit, row)} onClose={() => setOpen(null)} onChanged={() => void load()} />
      <AddIdeaPanel
        open={adding}
        quarterLabel={label}
        quarterId={data?.quarter.id ?? quarterId ?? ''}
        projects={data?.project_options ?? []}
        defaultProject={projectKey}
        onClose={() => setAdding(false)}
        onAdded={() => void load()}
      />
    </div>
  );
}
